<template>
  <div class="page-section tu-scope">
    <div :class="containerClass">
      <h2 class="mb-2">Mentors</h2>
      <p class="mb-4">Students and nurses who got in, plus advisors who can help you navigate your path.</p>

      <div class="tu-toolbar" role="search" aria-label="Filter people">
        <tu-filter-search :value="searchTerm" placeholder="Search by name" label="Search people by name" @input="onSearchInput" />
        <tu-filter-select
          v-if="isTu"
          :value="filters.uni_id"
          label="School"
          placeholder="All schools"
          :options="universityOptions"
          aria-label="Filter by school"
          @change="filters.uni_id = $event || null; fetchAdvisors()"
        />
        <tu-filter-select
          :value="filters.program"
          label="Program"
          placeholder="All programs"
          :options="programOptions"
          aria-label="Filter by program"
          @change="filters.program = $event || null; fetchAdvisors()"
        />
        <tu-filter-select
          :value="filters.background"
          label="Background"
          placeholder="Any background"
          :options="backgroundOptions"
          aria-label="Filter by background"
          @change="filters.background = $event || null; fetchAdvisors()"
        />
      </div>

      <div class="tu-results">
        <p class="tu-count" role="status" aria-live="polite">
          <template v-if="isLoading">Loading people…</template>
          <template v-else-if="totalAdvisors">Showing {{ pageFromCount }}–{{ pageToCount }} of {{ totalAdvisors }} people</template>
        </p>
        <div class="tu-results-tools">
          <button v-if="hasActiveFilters" type="button" class="tu-clear" @click="clearFilters">Clear filters <span aria-hidden="true">×</span></button>
        </div>
      </div>

      <div v-if="isLoading" class="tu-card-grid" aria-busy="true">
        <div v-for="i in 6" :key="`s-${i}`" class="tu-skel"></div>
      </div>

      <div v-else-if="!allAdvisors.length" class="tu-empty">
        <md-icon>people</md-icon>
        <h4 class="tu-empty-title">No mentors yet</h4>
        <p class="tu-empty-text">Try clearing a filter or expanding your search.</p>
        <button v-if="hasActiveFilters" type="button" class="tu-btn tu-btn--ghost tu-empty-cta" @click="clearFilters">Clear filters</button>
      </div>

      <div v-else class="tu-card-grid">
        <person-card v-for="advisor in allAdvisors" :key="advisor.id" :advisor="advisor" />
      </div>

      <pagination
        v-if="totalAdvisors > allAdvisors.length"
        v-model="currentPage"
        :total-rows="totalAdvisors"
        :per-page="perPage"
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import TuFilterSearch from '@/components/Ui/TuFilterSearch.vue';
import TuFilterSelect from '@/components/Ui/TuFilterSelect.vue';
import { ADVISOR_BACKGROUND, DASHBOARD_TYPES } from '@/common/constants';
import PersonCard from './PersonCard.vue';

/**
 * Student portal People page (advisors API). Same bar + results row + card
 * grid as the Pathways, Schools and Programs pages.
 */
export default {
  name: 'AdvisorList',
  components: { PersonCard, Pagination, TuFilterSearch, TuFilterSelect },
  extends: Page,

  data() {
    return {
      title: 'Mentors',
      isLoading: true,
      perPage: 15,
      currentPage: 1,
      totalAdvisors: 0,
      pageFromCount: 0,
      pageToCount: 0,
      allAdvisors: [],
      searchTerm: '',
      programs: [],
      universities: [],
      backgroundOptions: ADVISOR_BACKGROUND,
      filters: { program: null, background: null, uni_id: null },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUserDashboard', 'getLoggedInUser']),
    isTu() {
      return this.getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS;
    },
    programOptions() {
      return this.programs.map(p => ({ value: p.id, text: p.title }));
    },
    universityOptions() {
      return this.universities.map(u => ({ value: u.id, text: u.name }));
    },
    hasActiveFilters() {
      return !!(this.searchTerm || this.filters.program || this.filters.background || this.filters.uni_id);
    },
  },

  methods: {
    get,
    ...mapActions('advisors', ['getAllAdvisors']),
    ...mapActions('program', ['getAllPublicPrograms']),
    ...mapActions('uni', ['getAllUniversities']),

    onSearchInput(value) {
      this.searchTerm = value;
      this.debouncedSearch(this);
    },
    debouncedSearch: debounce(vm => vm.fetchAdvisors(), 400),

    clearFilters() {
      this.searchTerm = '';
      this.filters = { program: null, background: null, uni_id: null };
      this.fetchAdvisors();
    },

    async fetchAdvisors(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllAdvisors({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.isTu
            ? { entity_id: this.filters.uni_id, advisor_association: 'university' }
            : { entity_id: get(this.getLoggedInUser, 'linked_entity.id'), advisor_association: 'organization' }),
          ...(this.filters.program && { program: this.filters.program }),
          ...(this.filters.background && { background: this.filters.background }),
        });
        this.allAdvisors = response.data.results;
        this.currentPage = pageNum;
        this.totalAdvisors = response.data.count;
        this.pageFromCount = (this.currentPage - 1) * this.perPage + 1;
        this.pageToCount = this.pageFromCount + get(response.data, 'results.length', 0) - 1;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchAdvisors(pageNum);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async fetchOptions() {
      try {
        const response = await this.getAllPublicPrograms();
        const list = get(response, 'data.results', get(response, 'data', [])) || [];
        this.programs = Array.isArray(list) ? list : [];
      } catch (err) {
        this.programs = [];
      }
      if (this.isTu) {
        try {
          const response = await this.getAllUniversities({ limit: 300, ordering: 'name', basic: true });
          this.universities = get(response, 'data.results', []) || [];
        } catch (err) {
          this.universities = [];
        }
      }
    },
  },

  async mounted() {
    this.fetchOptions();
    await this.fetchAdvisors();
  },
};
</script>
